import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ROUTINE_CHECK_ITEMS } from './routineCheckItems';
import IssueDetailsModal from './IssueDetailsModal';
import { getRoutineCheckTemplate, addRoutineCheckTemplateItem, deleteRoutineCheckTemplateItem } from '../services/apiService';

const RoutineCheckForm = () => {
  const { vesselId } = useParams();
  const navigate = useNavigate();
  const [vessel, setVessel] = useState(null);
  const [checkItems, setCheckItems] = useState(ROUTINE_CHECK_ITEMS);
  const [saving, setSaving] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [activeIssueItemKey, setActiveIssueItemKey] = useState<string | null>(null);
  const [itemNotes, setItemNotes] = useState<Record<string, string>>({});
  const [itemPhotos, setItemPhotos] = useState<Record<string, string[]>>({});
  const [savedCheckId, setSavedCheckId] = useState<number | null>(null);
  const [savedItemIds, setSavedItemIds] = useState<Record<string, number>>({});
  const [draftSaving, setDraftSaving] = useState(false);

  const currentUser = JSON.parse(sessionStorage.getItem('auth_current_user') || '{}');
  const canEditTemplate = currentUser?.role === 'ADMIN' || currentUser?.role === 'TECHNICAL';
  const [newItemText, setNewItemText] = useState('');
  const [addingToCategory, setAddingToCategory] = useState<string | null>(null);

  const handleAddItem = async (category: string) => {
    const text = newItemText.trim();
    if (!text) return;
    const item_key = text.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_|_$/g, '') || ('CUSTOM_' + Date.now());
    const newItem = await addRoutineCheckTemplateItem({
      category,
      item_key,
      item_label_gr: text,
      item_label_en: text,
    });
    if (newItem) {
      setCheckItems((prev: any) => [...prev, {
        category: newItem.category,
        key: newItem.item_key,
        gr: newItem.item_label_gr,
        en: newItem.item_label_en,
        order: newItem.item_order,
        dbId: newItem.id,
      }]);
      setNewItemText('');
      setAddingToCategory(null);
    } else {
      alert('Αποτυχία προσθήκης.');
    }
  };

  const handleRemoveItem = async (item: any) => {
    if (!item.dbId) {
      alert('Δεν μπορεί να αφαιρεθεί.');
      return;
    }
    const label = item.gr || item.en || item.key;
    if (!window.confirm('Σίγουρα θέλεις να αφαιρεθεί το "' + label + '";')) return;
    const ok = await deleteRoutineCheckTemplateItem(item.dbId);
    if (ok) {
      setCheckItems((prev: any) => prev.filter((it: any) => it.dbId !== item.dbId));
    } else {
      alert('Αποτυχία αφαίρεσης.');
    }
  };

  useEffect(() => {
    (async () => {
      const dbItems = await getRoutineCheckTemplate();
      if (dbItems && dbItems.length > 0) {
        const mapped = dbItems.map((it: any) => ({
          category: it.category,
          key: it.item_key,
          gr: it.item_label_gr,
          en: it.item_label_en,
          order: it.item_order,
          dbId: it.id,
        }));
        setCheckItems(mapped);
      }
    })();
  }, []);

  // Signature state
  const sigCanvasRef = useRef<HTMLCanvasElement>(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [checkDateTime] = useState<Date>(new Date());

  const formatCheckDateTime = (d: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const getCanvasPoint = (e: any, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDrawing = (e: any) => {
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPoint(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: any) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPoint(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = sigCanvasRef.current;
    if (canvas) {
      setSignatureDataUrl(canvas.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureDataUrl('');
  };

  useEffect(() => {
    loadVessel();
  }, [vesselId]);

  const loadVessel = async () => {
    try {
      const response = await fetch('/api/vessels.php');
      if (response.ok) {
        const data = await response.json(); const vessels = data.vessels || data || [];
        const foundVessel = vessels.find(v => v.id === parseInt(vesselId || '0'));
        setVessel(foundVessel);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const autoSaveDraft = async (itemsToSave: any[]) => {
    if (savedCheckId || draftSaving) return savedCheckId;
    setDraftSaving(true);
    try {
      const checkData = {
        vessel_id: vessel.id,
        vessel_name: vessel.name,
        technician_code: currentUser.code || 'UNKNOWN',
        technician_name: currentUser.name || 'Unknown User',
        signature_image: '',
        summary_ok: itemsToSave.filter((i: any) => i.status === 'OK').length,
        summary_not_ok: itemsToSave.filter((i: any) => i.status === 'NOT_OK').length,
        items: itemsToSave.map((item: any, index: number) => ({
          category: item.category,
          item_key: item.key,
          item_label_gr: item.gr,
          item_label_en: item.en,
          status: item.status || '',
          note: itemNotes[item.key] || '',
          note_photos: itemPhotos[item.key] || [],
          item_order: index + 1,
          is_custom: false
        }))
      };
      const response = await fetch('/api/routine-checks.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkData)
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.id) {
          setSavedCheckId(data.id);
          if (Array.isArray(data.items)) {
            const map: Record<string, number> = {};
            for (const it of data.items) map[it.item_key] = it.id;
            setSavedItemIds(map);
          }
          return data.id;
        }
      }
    } catch (e) {
      console.error('autoSaveDraft failed', e);
    } finally {
      setDraftSaving(false);
    }
    return null;
  };

  const handleItemStatusChange = async (itemKey: string, status: string) => {
    const newItems = checkItems.map(item =>
      item.key === itemKey ? { ...item, status } : item
    );
    setCheckItems(newItems);
    if (status === 'NOT_OK') {
      if (!savedCheckId) {
        await autoSaveDraft(newItems);
      }
      setActiveIssueItemKey(itemKey);
      setShowIssueModal(true);
    }
  };

  const handleSave = async () => {
    if (!vessel) return;
    
    setSaving(true);
    try {
      const okCount = checkItems.filter(item => item.status === 'OK').length;
      const notOkCount = checkItems.filter(item => item.status === 'NOT_OK').length;

      const checkData = {
        vessel_id: vessel.id,
        vessel_name: vessel.name,
        technician_code: currentUser.code || 'UNKNOWN',
        technician_name: currentUser.name || 'Unknown User',
        signature_image: signatureDataUrl,
        summary_ok: okCount,
        summary_not_ok: notOkCount,
        items: checkItems.map((item, index) => ({
          category: item.category,
          item_key: item.key,
          item_label_gr: item.gr,
          item_label_en: item.en,
          status: item.status || '',
          note: itemNotes[item.key] || '',
          note_photos: itemPhotos[item.key] || [],
          item_order: index + 1,
          is_custom: false
        }))
      };

      const url = savedCheckId
        ? `/api/routine-checks.php?id=${savedCheckId}`
        : '/api/routine-checks.php';
      const method = savedCheckId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.id) setSavedCheckId(data.id);
        if (data && Array.isArray(data.items)) {
          const map: Record<string, number> = {};
          for (const it of data.items) map[it.item_key] = it.id;
          setSavedItemIds(map);
        }
        alert('Έλεγχος αποθηκεύτηκε! Μπορείς τώρα να ανεβάσεις φωτογραφίες στα NOT OK items πατώντας το 📝. Πάτησε "Ολοκλήρωση" όταν τελειώσεις.');
      } else {
        alert('Σφάλμα αποθήκευσης!');
      }
    } catch (error) {
      alert('Σφάλμα αποθήκευσης!');
    } finally {
      setSaving(false);
    }
  };

  if (!vessel) return <div>Φόρτωση...</div>;

  const externalItems = checkItems.filter(item => item.category === 'EXTERNAL');
  const internalItems = checkItems.filter(item => item.category === 'INTERNAL');

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-md p-4">
        <h1 className="text-2xl font-bold text-blue-800">ΕΛΕΓΧΟΣ ΡΟΥΤΙΝΑΣ — {vessel.name}</h1>
        <p className="text-gray-600">Τεχνικός: {currentUser.name}</p>
      </div>
      
      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">ΕΞΩΤΕΡΙΚΑ</h2>
            {externalItems.map(item => (
              <div key={item.key} className="flex justify-between p-3 border rounded mb-2">
                <span className="text-sm">{item.gr}</span>
                {canEditTemplate && item.dbId && (
                  <button onClick={(e) => { e.stopPropagation(); handleRemoveItem(item); }} style={{ marginLeft: 8, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} title="Αφαίρεση">🗑️</button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleItemStatusChange(item.key, 'OK')}
                    className={item.status === 'OK' ? 'bg-green-500 text-white px-3 py-1 rounded text-sm' : 'bg-gray-200 px-3 py-1 rounded text-sm hover:bg-green-100'}
                  >
                    ✅ OK
                  </button>
                  <button
                    onClick={() => handleItemStatusChange(item.key, 'NOT_OK')}
                    className={item.status === 'NOT_OK' ? 'bg-red-500 text-white px-3 py-1 rounded text-sm' : 'bg-gray-200 px-3 py-1 rounded text-sm hover:bg-red-100'}
                  >
                    ❌ NOT OK
                  </button>
                  {item.status === 'NOT_OK' && (
                    <button
                      type="button"
                      onClick={() => { setActiveIssueItemKey(item.key); setShowIssueModal(true); }}
                      className="ml-1 px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800 hover:bg-yellow-200 flex items-center gap-1"
                      title="Επεξεργασία προβλήματος"
                    >
                      📝
                      {(itemPhotos[item.key]?.length || 0) > 0 && (
                        <img
                          src={'/' + itemPhotos[item.key][0]}
                          alt=""
                          style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 4 }}
                        />
                      )}
                      {itemNotes[item.key] && (itemPhotos[item.key]?.length || 0) === 0 && (
                        <span style={{ fontSize: 10 }}>•</span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          {canEditTemplate && (
            <div style={{ marginTop: 8, padding: 8, borderTop: '1px dashed #cbd5e1' }}>
              {addingToCategory === 'EXTERNAL' ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input autoFocus value={newItemText} onChange={(e) => setNewItemText(e.target.value)} placeholder="Νέο item..." style={{ flex: 1, padding: '4px 8px', border: '1px solid #94a3b8', borderRadius: 4 }} />
                  <button onClick={() => handleAddItem('EXTERNAL')} style={{ padding: '4px 10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Αποθήκευση</button>
                  <button onClick={() => { setAddingToCategory(null); setNewItemText(''); }} style={{ padding: '4px 10px', background: '#e5e7eb', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Άκυρο</button>
                </div>
              ) : (
                <button onClick={() => setAddingToCategory('EXTERNAL')} style={{ padding: '6px 12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>+ Προσθήκη item</button>
              )}
            </div>
          )}
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">ΕΣΩΤΕΡΙΚΑ</h2>
            {internalItems.map(item => (
              <div key={item.key} className="flex justify-between p-3 border rounded mb-2">
                <span className="text-sm">{item.gr}</span>
                {canEditTemplate && item.dbId && (
                  <button onClick={(e) => { e.stopPropagation(); handleRemoveItem(item); }} style={{ marginLeft: 8, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} title="Αφαίρεση">🗑️</button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleItemStatusChange(item.key, 'OK')}
                    className={item.status === 'OK' ? 'bg-green-500 text-white px-3 py-1 rounded text-sm' : 'bg-gray-200 px-3 py-1 rounded text-sm hover:bg-green-100'}
                  >
                    ✅ OK
                  </button>
                  <button
                    onClick={() => handleItemStatusChange(item.key, 'NOT_OK')}
                    className={item.status === 'NOT_OK' ? 'bg-red-500 text-white px-3 py-1 rounded text-sm' : 'bg-gray-200 px-3 py-1 rounded text-sm hover:bg-red-100'}
                  >
                    ❌ NOT OK
                  </button>
                  {item.status === 'NOT_OK' && (
                    <button
                      type="button"
                      onClick={() => { setActiveIssueItemKey(item.key); setShowIssueModal(true); }}
                      className="ml-1 px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800 hover:bg-yellow-200 flex items-center gap-1"
                      title="Επεξεργασία προβλήματος"
                    >
                      📝
                      {(itemPhotos[item.key]?.length || 0) > 0 && (
                        <img
                          src={'/' + itemPhotos[item.key][0]}
                          alt=""
                          style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 4 }}
                        />
                      )}
                      {itemNotes[item.key] && (itemPhotos[item.key]?.length || 0) === 0 && (
                        <span style={{ fontSize: 10 }}>•</span>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          {canEditTemplate && (
            <div style={{ marginTop: 8, padding: 8, borderTop: '1px dashed #cbd5e1' }}>
              {addingToCategory === 'INTERNAL' ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input autoFocus value={newItemText} onChange={(e) => setNewItemText(e.target.value)} placeholder="Νέο item..." style={{ flex: 1, padding: '4px 8px', border: '1px solid #94a3b8', borderRadius: 4 }} />
                  <button onClick={() => handleAddItem('INTERNAL')} style={{ padding: '4px 10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Αποθήκευση</button>
                  <button onClick={() => { setAddingToCategory(null); setNewItemText(''); }} style={{ padding: '4px 10px', background: '#e5e7eb', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Άκυρο</button>
                </div>
              ) : (
                <button onClick={() => setAddingToCategory('INTERNAL')} style={{ padding: '6px 12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>+ Προσθήκη item</button>
              )}
            </div>
          )}
          </div>
        </div>

        {/* Signature Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6 max-w-2xl mx-auto">
          <h3 className="text-lg font-bold mb-3 text-center text-gray-700">Υπογραφή Τεχνικού</h3>
          <div className="border-2 border-gray-400 rounded-lg bg-white">
            <canvas
              ref={sigCanvasRef}
              width={600}
              height={150}
              className="w-full touch-none cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          <div className="flex justify-center mt-2">
            <button
              type="button"
              onClick={clearSignature}
              className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm"
            >
              Καθαρισμός Υπογραφής
            </button>
          </div>
          <p className="text-center text-gray-700 mt-3 text-sm font-semibold">
            {formatCheckDateTime(checkDateTime)}
          </p>
        </div>

        <div className="text-center space-x-4">
          <button
            onClick={() => navigate('/routine-check')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded"
          >
            Άκυρο
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded font-semibold"
          >
            {saving ? 'Αποθήκευση...' : 'ΑΠΟΘΗΚΕΥΣΗ'}
          </button>
        {savedCheckId && (
          <button
            type="button"
            onClick={() => navigate('/routine-check')}
            className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded font-semibold ml-2"
          >
            ✓ ΟΛΟΚΛΗΡΩΣΗ
          </button>
        )}
        </div>
      </div>

      <IssueDetailsModal
        isOpen={showIssueModal}
        itemKey={activeIssueItemKey || ''}
        itemLabel={(() => {
          const it = checkItems.find(x => x.key === activeIssueItemKey);
          return it ? (it.gr || it.en || '') : '';
        })()}
        checkId={savedCheckId}
        itemId={activeIssueItemKey ? (savedItemIds[activeIssueItemKey] || null) : null}
        initialNote={activeIssueItemKey ? (itemNotes[activeIssueItemKey] || '') : ''}
        initialPhotos={activeIssueItemKey ? (itemPhotos[activeIssueItemKey] || []) : []}
        lang="el"
        onClose={() => setShowIssueModal(false)}
        onSave={(note, photos) => {
          if (!activeIssueItemKey) return;
          setItemNotes(prev => ({ ...prev, [activeIssueItemKey]: note }));
          setItemPhotos(prev => ({ ...prev, [activeIssueItemKey]: photos }));
          setShowIssueModal(false);
        }}
      />
    </div>
  );
};

export default RoutineCheckForm;
